import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { collection, getDocs, doc, setDoc, query, orderBy, limit, startAfter } from 'firebase/firestore'
import { db, COLLECTIONS } from '../firebase/config'
import PageHelpButton from '../components/PageHelpButton'
import toast from 'react-hot-toast'
import { Users, RotateCcw, Mail, Phone, Search, Eye, X, Loader } from 'lucide-react'

const PAGE_SIZE = 50

const Customers = () => {
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastVisible, setLastVisible] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [activeCustomer, setActiveCustomer] = useState(null)
  const [filterName, setFilterName] = useState('')
  const [filterPhone, setFilterPhone] = useState('')

  // Normalize phone number for comparison (remove spaces, dashes, etc.)
  const normalizePhone = (phone) => {
    if (!phone) return ''
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '')
    // If starts with 0, replace with 233
    if (cleaned.startsWith('0')) {
      cleaned = '233' + cleaned.substring(1)
    } else if (!cleaned.startsWith('233') && cleaned.length > 0) {
      cleaned = '233' + cleaned
    }
    return cleaned
  }

  // Sync customers from bookings, gift cards (creators + recipients), and manual balance payments
  const syncCustomers = async () => {
    setSyncing(true)
    try {
      const customersMap = new Map()

      // 1. Fetch all bookings
      const bookingsRef = collection(db, COLLECTIONS.BOOKINGS)
      const bookingsSnapshot = await getDocs(bookingsRef)
      
      bookingsSnapshot.docs.forEach(doc => {
        const data = doc.data()
        const phone = normalizePhone(data.phone)
        const name = (data.name || '').trim()
        
        if (phone && name) {
          const key = phone
          if (!customersMap.has(key)) {
            customersMap.set(key, {
              phone: phone,
              name: name,
              email: '',
              source: 'booking',
              lastBookingDate: data.date || '',
              createdAt: data.createdAt || new Date().toISOString()
            })
          } else {
            // Update if we have newer booking date
            const existing = customersMap.get(key)
            if (data.date && (!existing.lastBookingDate || data.date > existing.lastBookingDate)) {
              existing.lastBookingDate = data.date
            }
            if (!existing.email) {
              existing.email = ''
            }
          }
        }
      })

      // 2. Fetch all gift cards (creator + recipient)
      const giftCardsRef = collection(db, COLLECTIONS.GIFT_CARDS)
      const giftCardsSnapshot = await getDocs(giftCardsRef)
      
      giftCardsSnapshot.docs.forEach(doc => {
        const data = doc.data()
        
        // Process creator (gift card buyer)
        const creatorPhone = normalizePhone(data.creatorPhone)
        const creatorName = (data.creatorName || '').trim() || (creatorPhone ? `Gift card buyer ${creatorPhone.slice(-6)}` : '')
        if (creatorPhone) {
          const key = creatorPhone
          if (!customersMap.has(key)) {
            customersMap.set(key, {
              phone: creatorPhone,
              name: creatorName,
              email: data.creatorEmail || '',
              source: 'gift_card_creator',
              lastBookingDate: '',
              createdAt: data.dateCreated || new Date().toISOString()
            })
          } else {
            const existing = customersMap.get(key)
            if (data.creatorEmail && !existing.email) existing.email = data.creatorEmail
            if (creatorName && !existing.name) existing.name = creatorName
          }
        }

        // Process recipient
        const recipientPhone = normalizePhone(data.recipientPhone)
        const recipientName = (data.recipientName || '').trim() || (recipientPhone ? `Gift card recipient ${recipientPhone.slice(-6)}` : '')
        if (recipientPhone) {
          const key = recipientPhone
          if (!customersMap.has(key)) {
            customersMap.set(key, {
              phone: recipientPhone,
              name: recipientName,
              email: data.recipientEmail || '',
              source: 'gift_card_recipient',
              lastBookingDate: '',
              createdAt: data.dateCreated || new Date().toISOString()
            })
          } else {
            const existing = customersMap.get(key)
            if (data.recipientEmail && !existing.email) existing.email = data.recipientEmail
            if (recipientName && !existing.name) existing.name = recipientName
          }
        }
      })

      // 3. Fetch all manual balance payments
      const manualPaymentsRef = collection(db, COLLECTIONS.MANUAL_PAYMENTS)
      const manualPaymentsSnapshot = await getDocs(manualPaymentsRef)
      
      manualPaymentsSnapshot.docs.forEach(doc => {
        const data = doc.data()
        const phone = normalizePhone(data.phone || data.customerPhone || '')
        const name = (data.name || data.customerName || '').trim() || (phone ? `Customer ${phone.slice(-6)}` : '')
        
        if (phone) {
          const key = phone
          if (!customersMap.has(key)) {
            customersMap.set(key, {
              phone: phone,
              name: name,
              email: '',
              source: 'manual_payment',
              lastBookingDate: '',
              createdAt: data.createdAt || data.date || new Date().toISOString()
            })
          } else {
            // Update source if this is a more recent entry
            const existing = customersMap.get(key)
            // If existing customer doesn't have email, keep it empty
            if (!existing.email) {
              existing.email = ''
            }
            // Update source to include manual payment if not already set
            if (existing.source !== 'manual_payment' && 
                existing.source !== 'booking' && 
                existing.source !== 'gift_card_creator' && 
                existing.source !== 'gift_card_recipient') {
              // If customer exists from other sources, we'll keep the original source
              // but could mark as multi-source if needed
            }
          }
        }
      })

      // Save all unique customers to Firestore
      const customersArray = Array.from(customersMap.values())
      const batchPromises = customersArray.map(async (customer) => {
        // Use phone as document ID for easy lookup
        const customerRef = doc(db, COLLECTIONS.CUSTOMERS, customer.phone)
        await setDoc(customerRef, {
          ...customer,
          updatedAt: new Date().toISOString()
        }, { merge: true })
      })

      await Promise.all(batchPromises)

      toast.success(`Synced ${customersArray.length} customers from bookings, gift cards, and manual payments`)
      
      // Reload customers
      fetchCustomers()
    } catch (error) {
      console.error('Error syncing customers:', error)
      toast.error('Failed to sync customers')
    } finally {
      setSyncing(false)
    }
  }

  const fetchCustomers = async (cursor = null, append = false) => {
    if (append) setLoadingMore(true)
    else setLoading(true)
    try {
      const customersRef = collection(db, COLLECTIONS.CUSTOMERS)
      const q = cursor
        ? query(customersRef, orderBy('updatedAt', 'desc'), limit(PAGE_SIZE), startAfter(cursor))
        : query(customersRef, orderBy('updatedAt', 'desc'), limit(PAGE_SIZE))
      const snapshot = await getDocs(q)
      const fetchedCustomers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null
      setLastVisible(lastDoc)
      setHasMore(snapshot.docs.length === PAGE_SIZE)
      if (append) setCustomers(prev => [...prev, ...fetchedCustomers])
      else setCustomers(fetchedCustomers)
    } catch (error) {
      console.error('Error fetching customers:', error)
      toast.error('Failed to load customers')
      setHasMore(false)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }

  const loadMoreCustomers = () => {
    if (!lastVisible || loadingMore) return
    fetchCustomers(lastVisible, true)
  }

  useEffect(() => {
    fetchCustomers()
  }, [])

  // Filter customers
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const matchesName = !filterName || 
        customer.name.toLowerCase().includes(filterName.toLowerCase())
      const matchesPhone = !filterPhone || 
        customer.phone.includes(filterPhone) ||
        customer.phone.replace(/\D/g, '').includes(filterPhone.replace(/\D/g, ''))
      return matchesName && matchesPhone
    })
  }, [customers, filterName, filterPhone])

  const openPreview = (customer) => {
    setActiveCustomer(customer)
    setShowModal(true)
  }

  const formatPhone = (phone) => {
    if (!phone) return 'N/A'
    // Format as 0XXXXXXXXX if it's 12 digits starting with 233
    if (phone.startsWith('233') && phone.length === 12) {
      return '0' + phone.substring(3)
    }
    return phone
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="text-gray-400 mt-3">Loading customers...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 bg-gray-900 min-h-screen text-white">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">Customer Management</h1>
        <button
          onClick={syncCustomers}
          disabled={syncing}
          className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RotateCcw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          <span>{syncing ? 'Syncing...' : 'Sync Customers'}</span>
        </button>
      </div>

      <PageHelpButton videoId={5} title="Customer Management tutorial" />

      {/* Stats - small, one row */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
          <Users className="w-4 h-4 text-blue-500" />
          <span className="text-gray-400 text-xs">Total Customers</span>
          <span className="text-sm font-semibold text-white">{customers.length}</span>
        </div>
        <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
          <Mail className="w-4 h-4 text-green-500" />
          <span className="text-gray-400 text-xs">With Email</span>
          <span className="text-sm font-semibold text-white">{customers.filter(c => c.email).length}</span>
        </div>
        <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-yellow-500" />
          <span className="text-gray-400 text-xs">Displayed</span>
          <span className="text-sm font-semibold text-white">{filteredCustomers.length}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Filter by Name</label>
          <input
            type="text"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder="Search by name..."
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Filter by Phone</label>
          <input
            type="text"
            value={filterPhone}
            onChange={(e) => setFilterPhone(e.target.value)}
            placeholder="Search by phone..."
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Customers Table */}
      {filteredCustomers.length === 0 ? (
        <div className="text-center p-8 bg-gray-800 rounded-lg">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400">
            {customers.length === 0 
              ? 'No customers found. Click "Sync Customers" to pull from bookings, gift card buyers/recipients, and manual balance payments.'
              : 'No customers match your filters.'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-gray-800 rounded-lg shadow-lg">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Last Booking</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredCustomers.map(customer => (
                  <tr key={customer.id} className="hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button 
                        onClick={() => openPreview(customer)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">
                      {customer.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4" />
                        <span>{formatPhone(customer.phone)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {customer.email ? (
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4" />
                          <span>{customer.email}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {customer.source === 'booking' ? 'Booking' : 
                         customer.source === 'gift_card_creator' ? 'Gift Card Creator' :
                         customer.source === 'gift_card_recipient' ? 'Gift Card Recipient' :
                         customer.source === 'manual_payment' ? 'Manual Payment' : 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {customer.lastBookingDate ? (
                        new Date(customer.lastBookingDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && customers.length > 0 && (
            <div className="border-t border-gray-700 px-6 py-3 flex items-center justify-between bg-gray-800/50">
              <p className="text-xs text-gray-400">Load more only when needed to reduce reads.</p>
              <button
                type="button"
                onClick={loadMoreCustomers}
                disabled={!hasMore || loadingMore}
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : hasMore ? (
                  <span>Load previous customers</span>
                ) : (
                  <span>No more</span>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Preview Modal - portaled so header is below top bar */}
      {showModal && activeCustomer && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-start justify-center p-4 pt-20 overflow-y-auto"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full border border-gray-700 my-auto max-h-[calc(100vh-5rem)] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Customer Details</h3>
              <button 
                onClick={() => setShowModal(false)} 
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-400">Name</p>
                <p className="text-white font-semibold text-lg">{activeCustomer.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Phone</p>
                <p className="text-white">{formatPhone(activeCustomer.phone)}</p>
              </div>
              {activeCustomer.email && (
                <div>
                  <p className="text-sm text-gray-400">Email</p>
                  <p className="text-white">{activeCustomer.email}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-400">Source</p>
                <p className="text-white">
                  {activeCustomer.source === 'booking' ? 'Booking' : 
                   activeCustomer.source === 'gift_card_creator' ? 'Gift Card Creator' :
                   activeCustomer.source === 'gift_card_recipient' ? 'Gift Card Recipient' :
                   activeCustomer.source === 'manual_payment' ? 'Manual Payment' : 'Unknown'}
                </p>
              </div>
              {activeCustomer.lastBookingDate && (
                <div>
                  <p className="text-sm text-gray-400">Last Booking Date</p>
                  <p className="text-white">
                    {new Date(activeCustomer.lastBookingDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-400">Customer ID</p>
                <p className="text-white font-mono text-sm">{activeCustomer.id}</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Customers
